import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box } from 'theme-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar, A11y } from 'swiper';
import FsLightbox from 'fslightbox-react';

import store, { useRecoilValue } from '@store';
import { useSelectedOptions, useMatchMedia } from '@hooks';
import { ShopifyMedia, Badge, Svg } from '@snippets';

import { Thumbs } from './Thumbs';
import { themed } from './Media.theme';

export const Media = themed(
  ({ theme, product, selectedVariant, aboveTheFold }) => {
    const selectedMedia = useRecoilValue(store.selectedMedia$);
    const [selectedOptions] = useSelectedOptions();
    const [activeIndex, setActiveIndex] = useState(0);
    const [swiper, setSwiper] = useState();
    const goToSlide = useCallback(
      (index) => {
        swiper.slideTo(index);
        setActiveIndex(index);
      },
      [swiper]
    );
    const isMobileViewport = useMatchMedia('(max-width: 767px)');

    /* Gallery Zoom */
    const [lightboxController, setLightboxController] = useState({
      toggler: false,
      slide: 1,
    });
    const handleFsLightbox = (idx) => {
      setLightboxController({
        toggler: !lightboxController.toggler,
        slide: idx,
      });
    };

    const { sourcesFSLB = [], thumnsFSLB = [] } = useMemo(() => {
      if (!selectedMedia) return { sourcesFSLB: [], thumnsFSLB: [] };
      const getSizedImageUrl = ({ src, width, height, crop }) => {
        if (!src) {
          return '';
        }
        const url = new URL(src);
        if (width) {
          url.searchParams.append('width', Math.round(width).toString());
        }
        if (height) {
          url.searchParams.append('height', Math.round(height).toString());
        }
        if (crop) {
          url.searchParams.append('crop', crop);
        }
        return url.href;
      };
      return {
        sourcesFSLB: (selectedMedia || []).reduce((acc, media, index) => {
          if (media?.mediaContentType === 'IMAGE') {
            return [...acc, media?.previewImage?.url || media?.image?.src];
          }
          if (media?.mediaContentType === 'VIDEO') {
            const videoMp4Url = (media?.sources || []).find(
              (v) => v?.format === 'mp4'
            )?.url;
            if (
              media?.video?.src.includes('youtube.com') ||
              media?.video?.src.includes('.mp4') ||
              videoMp4Url
            ) {
              const videoUrl = media?.video?.url
                ? media.video.url
                : videoMp4Url;
              if (videoUrl) {
                return [...acc, videoUrl];
              }
            }
            if (media?.video?.src.includes('vimeo.com')) {
              const iframe = (
                <iframe
                  src={`${media?.video?.src}`}
                  className="pdp-slider-iframe"
                  width="1920"
                  height="1080"
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title={`vimeo video ${index}`}
                />
              );
              return [...acc, iframe];
            }
          }
          return acc;
        }, []),
        thumnsFSLB: (selectedMedia || []).map((media) => {
          return getSizedImageUrl({
            src: media?.previewImage?.url || media?.image?.src,
            width: '115',
            height: '115',
            crop: 'center',
          });
        }),
      };
    }, [selectedMedia]);

    useEffect(() => {
      if (!swiper || !swiper.initialized) return undefined;
      const handleSlideChange = (_swiper) => {
        setActiveIndex(_swiper.realIndex);
      };
      swiper.on('slideChange', handleSlideChange);
      return () => {
        swiper.off('slideChange', handleSlideChange);
      };
    }, [swiper]);

    // on color swatch change go back to first slide
    useEffect(() => {
      if (!swiper || !swiper.initialized) return;
      if (!product) return;
      if (product.isGrouped) {
        swiper.slideTo(0);
        return;
      }

      const variantImageIndex = product.media?.findIndex(({ image }) => {
        return image?.id === selectedVariant?.image?.id;
      });
      if (variantImageIndex >= 0) {
        swiper.slideTo(variantImageIndex);
      }
    }, [selectedOptions?.Color, product?.id, swiper]);

    return (
      <>
        <Box data-comp={Media.displayName} sx={theme.medias}>
          {/* Thumbs */}
          {!isMobileViewport && (
            <Thumbs
              medias={selectedMedia}
              activeIndex={activeIndex}
              goToSlide={goToSlide}
              isMobileViewport={isMobileViewport}
            />
          )}
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              pb: [10, null, 0],
            }}
          >
            {/* Carousel */}
            <Swiper
              data-comp="MediaSwiper"
              modules={[Navigation, Scrollbar, A11y]}
              pagination={{
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
              }}
              preloadImages
              fadeEffect={{ crossFade: true }}
              onSwiper={setSwiper}
              slidesPerView={1}
              freeMode={false}
              sx={{
                ...theme.swiper,
                cursor: selectedMedia?.length > 1 ? 'grab' : 'auto',
              }}
              watchOverflow
            >
              {/* Ech large media */}
              {selectedMedia?.map((media, index) => {
                const isPrimaryIamge = index === 0 && aboveTheFold;

                return (
                  <SwiperSlide key={index} virtualIndex={index}>
                    <div
                      onClick={() => handleFsLightbox(index + 1)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFsLightbox(index + 1);
                        }
                      }}
                      className="relative group"
                      role="button"
                      tabIndex="0"
                      aria-label={`Zoom In ${
                        media.alt || `image ${index + 1}`
                      }`}
                    >
                      <ShopifyMedia
                        media={media}
                        bgColor="lightGray"
                        aboveTheFold={isPrimaryIamge}
                        preload={isPrimaryIamge}
                      />
                      <button
                        className="absolute bottom-3 md:bottom-5 right-4 md:right-5 flex size-6 md:size-10 items-center justify-center rounded-full bg-white md:opacity-0 md:group-hover:opacity-100"
                        onClick={() => handleFsLightbox(index + 1)}
                        type="button"
                        tabIndex="-1"
                      >
                        <Svg
                          className="w-3 md:w-5 shrink-0"
                          src="/svgs/icon-zoom.svg#icon-zoom"
                          title="Zoom In"
                          viewBox="0 0 21 23"
                        />
                      </button>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {/* Placeholder */}
            {!swiper && (
              <Box
                sx={{
                  width: '100%',
                  pb: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bg: 'lightGray',
                  zIndex: 1,
                }}
              />
            )}

            <Badge handle={product?.handle} product={product} isPdp />

            {/* Bullets */}
            <div className="swiper-pagination" sx={theme.bullets} />
          </Box>
        </Box>
        {selectedMedia?.length > 0 ? (
          <FsLightbox
            toggler={lightboxController.toggler}
            sources={sourcesFSLB}
            thumbs={thumnsFSLB}
            showThumbsOnMount
            slide={lightboxController.slide}
          />
        ) : null}
      </>
    );
  }
);

Media.displayName = 'Media';
